/**
 * PlotBuilder — "plot anything vs anything" creation panel.
 *
 * Sidebar has collapsible sections for every visual knob.
 * Main area shows live preview with selected engine.
 * Fields are auto-detected from the loaded project data.
 */

import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { useTheme } from '../../context/ThemeContext';
import { usePlotData } from '../../context/PlotDataContext';
import { useProvenanceContext } from '../../context/ProvenanceContext';
import { useProvenanceExportStore } from '../../stores/provenanceExportStore';
import { exportWithProvenance, downloadBlob } from '../data-explorer/exportAdapter';
import type { TagMode } from '../provenance/ProvenanceTag';
import { getRenderer } from './renderers';
import {
  type PlotBuilderConfig,
  type PlotEngine,
  type DataSourceMode,
  type AxisScale,
  type MarkerShape,
  type LineStyle,
  type BarMode,
  type LegendPosition,
  type ColorScale,
  type TrendLineType,
  type FontWeight,
  type VisualMapping,
  type MappingMode,
  createDefaultConfig,
  createDefaultMapping,
  CHART_TYPE_GROUPS,
  CHART_TYPE_LABELS,
  CHART_TYPE_ICONS,
  ENGINE_LABELS,
  getCompatibleEngines,
  getDefaultEngine,
  PALETTES,
  COLOR_SCALE_OPTIONS,
  MARKER_SHAPE_OPTIONS,
  LINE_STYLE_OPTIONS,
  BAR_MODE_OPTIONS,
  LEGEND_POSITION_OPTIONS,
  TREND_LINE_OPTIONS,
  FONT_WEIGHT_OPTIONS,
} from './types';

// ---------------------------------------------------------------------------
// Reusable style helpers
// ---------------------------------------------------------------------------

const sel = (theme: 'light' | 'dark'): React.CSSProperties => ({
  padding: '3px 6px', fontSize: 11,
  border: `1px solid ${theme === 'dark' ? '#363D39' : '#D0D5D2'}`,
  borderRadius: 4,
  background: theme === 'dark' ? '#1C211E' : '#FFFFFF',
  color: theme === 'dark' ? '#F0F2F0' : '#111813',
  fontFamily: 'var(--fc-font-family)', cursor: 'pointer', width: '100%',
});

const inp = (theme: 'light' | 'dark'): React.CSSProperties => ({ ...sel(theme), cursor: 'text' });

const numInp = (theme: 'light' | 'dark'): React.CSSProperties => ({
  ...sel(theme), cursor: 'text', width: 60,
});

const lbl: React.CSSProperties = {
  display: 'block', fontSize: 10, fontWeight: 600,
  textTransform: 'uppercase', letterSpacing: '0.05em', opacity: 0.6, marginBottom: 2,
};

const row: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 };
const halfRow: React.CSSProperties = { display: 'flex', gap: 4, marginTop: 4 };

// ---------------------------------------------------------------------------
// Collapsible section
// ---------------------------------------------------------------------------

function Section({ title, children, theme, defaultOpen = true }: {
  title: string; children: React.ReactNode; theme: 'light' | 'dark'; defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);
  const border = theme === 'dark' ? '#2A302C' : '#E6E9E7';
  return (
    <div style={{ borderBottom: `1px solid ${border}` }}>
      <button
        onClick={() => setOpen(!open)}
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          width: '100%', padding: '6px 12px', border: 'none', background: 'none',
          color: theme === 'dark' ? '#D1D9D3' : '#111813', cursor: 'pointer',
          fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em',
          fontFamily: 'var(--fc-font-family)',
        }}
      >
        {title}
        <span style={{ fontSize: 10, opacity: 0.5 }}>{open ? '▾' : '▸'}</span>
      </button>
      {open && <div style={{ padding: '0 12px 8px' }}>{children}</div>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Visual mapping control
// ---------------------------------------------------------------------------

function MappingControl({ label, mapping, fields, theme, onChange }: {
  label: string;
  mapping: VisualMapping;
  fields: string[];
  theme: 'light' | 'dark';
  onChange: (m: VisualMapping) => void;
}) {
  const border = theme === 'dark' ? '#363D39' : '#D0D5D2';
  return (
    <div style={{ marginBottom: 8 }}>
      <div style={{ fontSize: 10, fontWeight: 600, opacity: 0.6, marginBottom: 2, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
        {label}
      </div>
      <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
        <select
          value={mapping.mode}
          onChange={(e) => {
            const mode = e.target.value as MappingMode;
            onChange(mode === 'fixed'
              ? { ...createDefaultMapping(), mode: 'fixed' }
              : { ...mapping, mode: 'byVariable', field: mapping.field || fields[0] || '' }
            );
          }}
          style={{
            padding: '2px 4px', fontSize: 10,
            border: `1px solid ${border}`, borderRadius: 3,
            background: theme === 'dark' ? '#1C211E' : '#FFFFFF',
            color: theme === 'dark' ? '#F0F2F0' : '#111813',
            fontFamily: 'var(--fc-font-family)', cursor: 'pointer', width: 65,
          }}
        >
          <option value="fixed">Fixed</option>
          <option value="byVariable">Variable</option>
        </select>
        {mapping.mode === 'byVariable' && (
          <select
            value={mapping.field || ''}
            onChange={(e) => onChange({ ...mapping, field: e.target.value })}
            style={{
              padding: '2px 4px', fontSize: 10, flex: 1,
              border: `1px solid ${border}`, borderRadius: 3,
              background: theme === 'dark' ? '#1C211E' : '#FFFFFF',
              color: theme === 'dark' ? '#F0F2F0' : '#111813',
              fontFamily: 'var(--fc-font-family)', cursor: 'pointer',
            }}
          >
            {fields.map((f) => <option key={f} value={f}>{f}</option>)}
          </select>
        )}
      </div>
      {mapping.mode === 'byVariable' && (
        <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: 4 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 3, fontSize: 10, cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={mapping.bin}
              onChange={(e) => onChange({ ...mapping, bin: e.target.checked })}
            />
            Bin
          </label>
          {mapping.bin && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
              <input
                type="range" min={2} max={20} value={mapping.binCount}
                onChange={(e) => onChange({ ...mapping, binCount: Number(e.target.value) })}
                style={{ width: 60 }}
              />
              <span style={{ fontSize: 10, width: 18, textAlign: 'right' }}>{mapping.binCount}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

type PlotScope = "branch" | "project";

interface PlotBuilderProps {
  projectId: string;
  onSave?: (config: PlotBuilderConfig, data: Record<string, unknown>[], thumbnail?: string, scope?: PlotScope) => void;
  initialConfig?: PlotBuilderConfig;
  onPointClick?: (row: Record<string, unknown>) => void;
  selectedNodeId?: string | null;
}

export function PlotBuilder({ onSave, initialConfig, onPointClick, selectedNodeId }: PlotBuilderProps) {
  const { theme } = useTheme();
  const { fullData, filteredData } = usePlotData();
  const { projectId, currentBranch, selectedArtifactId } = useProvenanceContext();
  const { tagMode, setTagMode } = useProvenanceExportStore();
  const [config, setConfig] = useState<PlotBuilderConfig>(initialConfig ?? createDefaultConfig);
  const [scope, setScope] = useState<PlotScope>(currentBranch === "main" ? "project" : "branch");
  const [exportState, setExportState] = useState<'idle' | 'exporting' | 'done' | 'error'>('idle');
  const previewRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (initialConfig) setConfig(initialConfig);
  }, [initialConfig]);

  const compatibleEngines = useMemo(() => getCompatibleEngines(config.chartType), [config.chartType]);

  // Auto-detect fields from data
  const { numericFields, allFields } = useMemo(() => {
    if (fullData.length === 0) return { numericFields: [] as string[], allFields: [] as string[] };
    const first = fullData[0] as Record<string, unknown>;
    const keys = Object.keys(first).filter((k) => k !== 'id' && !k.startsWith('_'));
    const numeric = keys.filter((k) => typeof first[k] === 'number');
    return { numericFields: numeric, allFields: keys };
  }, [fullData]);

  // Auto-assign first two numeric fields as X/Y when data loads
  useEffect(() => {
    if (numericFields.length >= 2 && !config.xAxis.field) {
      setConfig((prev) => ({
        ...prev,
        xAxis: { ...prev.xAxis, field: numericFields[0]! },
        yAxis: { ...prev.yAxis, field: numericFields[1]! },
        title: `${numericFields[1]} vs ${numericFields[0]}`,
      }));
    }
  }, [numericFields, config.xAxis.field]);

  const chartData = config.dataSource === 'filtered' ? filteredData : fullData;
  const Renderer = getRenderer(config.engine);

  const needsZ = ['contour', 'heatmap', 'surface3d', 'scatter3d'].includes(config.chartType);
  const isLine = ['line', 'area'].includes(config.chartType);
  const isBar = ['bar', 'histogram'].includes(config.chartType);
  const isBubble = config.chartType === 'bubble';
  const isContinuousColor = ['contour', 'heatmap', 'surface3d'].includes(config.chartType);

  const update = useCallback(
    (patch: Partial<PlotBuilderConfig>) => {
      setConfig((prev) => {
        const next = { ...prev, ...patch };
        if (patch.chartType && !getCompatibleEngines(patch.chartType).includes(next.engine)) {
          next.engine = getDefaultEngine(patch.chartType);
        }
        return next;
      });
    },
    [],
  );

  const [saveState, setSaveState] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');

  const captureThumbnail = useCallback(async (): Promise<string | undefined> => {
    const container = previewRef.current;
    if (!container) return undefined;
    try {
      const plotlyDiv = container.querySelector('.js-plotly-plot') as HTMLElement | null;
      if (plotlyDiv) {
        const mod = await import('plotly.js');
        const Plotly = mod.default ?? mod;
        return await Plotly.toImage(plotlyDiv, { format: 'png', width: 400, height: 250 });
      }
      const svg = container.querySelector('svg');
      if (svg) {
        const svgData = new XMLSerializer().serializeToString(svg);
        const canvas = document.createElement('canvas');
        canvas.width = 400;
        canvas.height = 250;
        const ctx = canvas.getContext('2d');
        if (!ctx) return undefined;
        const img = new Image();
        return await new Promise<string | undefined>((resolve) => {
          img.onload = () => { ctx.drawImage(img, 0, 0, 400, 250); resolve(canvas.toDataURL('image/png')); };
          img.onerror = () => resolve(undefined);
          img.src = `data:image/svg+xml;base64,${btoa(unescape(encodeURIComponent(svgData)))}`;
        });
      }
    } catch { /* thumbnail capture is non-critical */ }
    return undefined;
  }, []);

  const handleSave = useCallback(async () => {
    if (!onSave || saveState === 'saving') return;
    setSaveState('saving');
    try {
      const thumbnail = await captureThumbnail();
      await onSave(config, chartData, thumbnail, scope);
      setSaveState('saved');
      setTimeout(() => setSaveState('idle'), 2000);
    } catch (err) {
      console.error('Failed to save plot:', err);
      setSaveState('error');
      setTimeout(() => setSaveState('idle'), 3000);
    }
  }, [onSave, config, chartData, saveState, captureThumbnail, scope]);

  const handleExport = useCallback(async () => {
    if (exportState === 'exporting') return;
    setExportState('exporting');
    try {
      const container = previewRef.current;
      if (!container) throw new Error('No plot container');

      let imageBlob: Blob | null = null;

      // Try Plotly first
      const plotlyDiv = container.querySelector('.js-plotly-plot') as HTMLElement | null;
      if (plotlyDiv) {
        const mod = await import('plotly.js');
        const Plotly = mod.default ?? mod;
        const dataUrl = await Plotly.toImage(plotlyDiv, {
          format: 'png',
          width: plotlyDiv.clientWidth * (config.dpi / 72),
          height: plotlyDiv.clientHeight * (config.dpi / 72),
          scale: 1,
        });
        const resp = await fetch(dataUrl);
        imageBlob = await resp.blob();
      } else {
        // Fallback: capture SVG or canvas
        const svg = container.querySelector('svg');
        if (svg) {
          const svgData = new XMLSerializer().serializeToString(svg);
          const canvas = document.createElement('canvas');
          canvas.width = svg.clientWidth * 2;
          canvas.height = svg.clientHeight * 2;
          const ctx = canvas.getContext('2d');
          if (ctx) {
            const img = new Image();
            await new Promise<void>((resolve, reject) => {
              img.onload = () => { ctx.drawImage(img, 0, 0, canvas.width, canvas.height); resolve(); };
              img.onerror = () => reject(new Error('SVG render failed'));
              img.src = `data:image/svg+xml;base64,${btoa(unescape(encodeURIComponent(svgData)))}`;
            });
            imageBlob = await new Promise<Blob>((resolve, reject) => {
              canvas.toBlob((b) => b ? resolve(b) : reject(new Error('toBlob failed')), 'image/png');
            });
          }
        }
      }

      if (!imageBlob) throw new Error('Could not capture plot image');

      const artifactId = selectedArtifactId || `plot-${Date.now()}`;
      const tagged = await exportWithProvenance({
        imageBlob,
        artifactId,
        projectId,
        tagMode,
        format: 'png',
      });

      const filename = `${config.title || 'plot'}-thread.png`.replace(/[^a-zA-Z0-9._-]/g, '_');
      downloadBlob(tagged, filename);
      setExportState('done');
      setTimeout(() => setExportState('idle'), 2000);
    } catch (err) {
      console.error('Export failed:', err);
      setExportState('error');
      setTimeout(() => setExportState('idle'), 3000);
    }
  }, [exportState, config, tagMode, selectedArtifactId, projectId]);

  const sidebarBg = theme === 'dark' ? '#161A18' : '#F8F9F8';
  const borderColor = theme === 'dark' ? '#2A302C' : '#E6E9E7';

  return (
    <div style={{ display: 'flex', height: '100%', width: '100%', fontFamily: 'var(--fc-font-family)' }}>
      {/* SIDEBAR */}
      <div style={{
        width: 260, minWidth: 260,
        borderRight: `1px solid ${borderColor}`,
        background: sidebarBg, fontSize: 12,
        color: theme === 'dark' ? '#D1D9D3' : '#111813',
        display: 'flex', flexDirection: 'column',
      }}>

        {/* Scrollable sections */}
        <div style={{ flex: 1, overflowY: 'auto' }}>
        {/* Chart Type */}
        <Section title="Chart Type" theme={theme}>
          {Object.entries(CHART_TYPE_GROUPS).map(([group, types]) => (
            <div key={group} style={{ marginBottom: 6 }}>
              <div style={{ fontSize: 9, fontWeight: 700, opacity: 0.4, marginBottom: 3 }}>{group}</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                {types.map((ct) => (
                  <button key={ct} onClick={() => update({ chartType: ct })} title={CHART_TYPE_LABELS[ct]}
                    style={{
                      padding: '3px 6px', fontSize: 10,
                      border: `1px solid ${config.chartType === ct ? '#00643C' : borderColor}`,
                      borderRadius: 4,
                      background: config.chartType === ct ? (theme === 'dark' ? '#0A3822' : '#E6F4ED') : 'transparent',
                      color: theme === 'dark' ? '#D1D9D3' : '#111813', cursor: 'pointer',
                      fontFamily: 'var(--fc-font-family)',
                    }}
                  >
                    {CHART_TYPE_ICONS[ct]} {CHART_TYPE_LABELS[ct]}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </Section>

        {/* Engine */}
        <Section title="Engine" theme={theme}>
          <select value={config.engine} onChange={(e) => update({ engine: e.target.value as PlotEngine })} style={sel(theme)}>
            {compatibleEngines.map((eng) => <option key={eng} value={eng}>{ENGINE_LABELS[eng]}</option>)}
          </select>
        </Section>

        {/* Data Source */}
        <Section title="Data Source" theme={theme}>
          <select value={config.dataSource} onChange={(e) => update({ dataSource: e.target.value as DataSourceMode })} style={sel(theme)}>
            <option value="full">Full Dataset ({fullData.length} rows)</option>
            <option value="filtered">AG Grid Filter ({filteredData.length} rows)</option>
          </select>
        </Section>

        {/* X Axis */}
        <Section title="X Axis" theme={theme}>
          <select value={config.xAxis.field} onChange={(e) => update({ xAxis: { ...config.xAxis, field: e.target.value } })} style={sel(theme)}>
            {numericFields.map((f) => <option key={f} value={f}>{f}</option>)}
          </select>
          <div style={halfRow}>
            <select value={config.xAxis.scale} onChange={(e) => update({ xAxis: { ...config.xAxis, scale: e.target.value as AxisScale } })} style={{ ...sel(theme), width: '50%' }}>
              <option value="linear">Linear</option>
              <option value="log">Log</option>
              <option value="reversed">Reversed</option>
            </select>
            <input placeholder="Label" value={config.xAxis.label || ''} onChange={(e) => update({ xAxis: { ...config.xAxis, label: e.target.value || undefined } })} style={{ ...inp(theme), width: '50%' }} />
          </div>
          <div style={halfRow}>
            <input type="number" placeholder="Min" value={config.xAxis.min ?? ''} onChange={(e) => update({ xAxis: { ...config.xAxis, min: e.target.value ? Number(e.target.value) : undefined } })} style={numInp(theme)} />
            <input type="number" placeholder="Max" value={config.xAxis.max ?? ''} onChange={(e) => update({ xAxis: { ...config.xAxis, max: e.target.value ? Number(e.target.value) : undefined } })} style={numInp(theme)} />
            <select value={config.xAxisTickAngle} onChange={(e) => update({ xAxisTickAngle: Number(e.target.value) })} style={{ ...sel(theme), width: 50 }} title="Tick angle">
              <option value={0}>0°</option><option value={45}>45°</option><option value={90}>90°</option>
            </select>
          </div>
        </Section>

        {/* Y Axis */}
        <Section title="Y Axis" theme={theme}>
          <select value={config.yAxis.field} onChange={(e) => update({ yAxis: { ...config.yAxis, field: e.target.value } })} style={sel(theme)}>
            {numericFields.map((f) => <option key={f} value={f}>{f}</option>)}
          </select>
          <div style={halfRow}>
            <select value={config.yAxis.scale} onChange={(e) => update({ yAxis: { ...config.yAxis, scale: e.target.value as AxisScale } })} style={{ ...sel(theme), width: '50%' }}>
              <option value="linear">Linear</option>
              <option value="log">Log</option>
              <option value="reversed">Reversed</option>
            </select>
            <input placeholder="Label" value={config.yAxis.label || ''} onChange={(e) => update({ yAxis: { ...config.yAxis, label: e.target.value || undefined } })} style={{ ...inp(theme), width: '50%' }} />
          </div>
          <div style={halfRow}>
            <input type="number" placeholder="Min" value={config.yAxis.min ?? ''} onChange={(e) => update({ yAxis: { ...config.yAxis, min: e.target.value ? Number(e.target.value) : undefined } })} style={numInp(theme)} />
            <input type="number" placeholder="Max" value={config.yAxis.max ?? ''} onChange={(e) => update({ yAxis: { ...config.yAxis, max: e.target.value ? Number(e.target.value) : undefined } })} style={numInp(theme)} />
            <select value={config.yAxisTickAngle} onChange={(e) => update({ yAxisTickAngle: Number(e.target.value) })} style={{ ...sel(theme), width: 50 }} title="Tick angle">
              <option value={0}>0°</option><option value={45}>45°</option><option value={90}>90°</option>
            </select>
          </div>
        </Section>

        {/* Z Axis (3D / 2.5D) */}
        {needsZ && (
          <Section title="Z Axis / Color" theme={theme}>
            <select value={config.zAxis?.field || numericFields[0] || ''} onChange={(e) => update({ zAxis: { field: e.target.value, scale: 'linear' } })} style={sel(theme)}>
              {numericFields.map((f) => <option key={f} value={f}>{f}</option>)}
            </select>
          </Section>
        )}

        {/* Group By */}
        <Section title="Group / Color" theme={theme}>
          <div style={lbl}>Group By</div>
          <select value={config.groupBy || ''} onChange={(e) => update({ groupBy: e.target.value || undefined })} style={sel(theme)}>
            <option value="">None</option>
            {allFields.map((f) => <option key={f} value={f}>{f}</option>)}
          </select>
          {isBubble && (
            <>
              <div style={{ ...lbl, marginTop: 6 }}>Size Field (Bubble)</div>
              <select value={config.sizeField || ''} onChange={(e) => update({ sizeField: e.target.value || undefined })} style={sel(theme)}>
                <option value="">None (uniform)</option>
                {numericFields.map((f) => <option key={f} value={f}>{f}</option>)}
              </select>
            </>
          )}
        </Section>

        {/* Visual Mapping */}
        <Section title="Visual Mapping" theme={theme}>
          <MappingControl label="Color by" mapping={config.colorMapping ?? createDefaultMapping()} fields={allFields} theme={theme}
            onChange={(m) => update({ colorMapping: m })} />
          <MappingControl label="Size by" mapping={config.sizeMapping ?? createDefaultMapping()} fields={numericFields} theme={theme}
            onChange={(m) => update({ sizeMapping: m })} />
          <MappingControl label="Shape by" mapping={config.shapeMapping ?? createDefaultMapping()} fields={allFields} theme={theme}
            onChange={(m) => update({ shapeMapping: m })} />
        </Section>

        {/* Title */}
        <Section title="Title" theme={theme}>
          <input value={config.title} onChange={(e) => update({ title: e.target.value })} style={inp(theme)} />
          <div style={halfRow}>
            <input type="number" min={8} max={28} value={config.titleSize} onChange={(e) => update({ titleSize: Number(e.target.value) })} style={numInp(theme)} title="Font size" />
            <select value={config.titleWeight} onChange={(e) => update({ titleWeight: e.target.value as FontWeight })} style={{ ...sel(theme), flex: 1 }}>
              {FONT_WEIGHT_OPTIONS.map((w) => <option key={w} value={w}>{w}</option>)}
            </select>
          </div>
        </Section>

        {/* Color Palette */}
        <Section title="Colors" theme={theme}>
          <div style={lbl}>Palette</div>
          <select value={config.palette} onChange={(e) => update({ palette: e.target.value })} style={sel(theme)}>
            {Object.keys(PALETTES).map((k) => <option key={k} value={k}>{k}</option>)}
          </select>
          <div style={{ display: 'flex', gap: 2, marginTop: 4 }}>
            {(PALETTES[config.palette] || []).slice(0, 8).map((c, i) => (
              <div key={i} style={{ width: 16, height: 12, borderRadius: 2, background: c }} />
            ))}
          </div>
          {isContinuousColor && (
            <>
              <div style={{ ...lbl, marginTop: 8 }}>Color Scale (continuous)</div>
              <select value={config.colorScale} onChange={(e) => update({ colorScale: e.target.value as ColorScale })} style={sel(theme)}>
                {COLOR_SCALE_OPTIONS.map((cs) => <option key={cs} value={cs}>{cs}</option>)}
              </select>
              <label style={{ ...row, marginTop: 4, fontSize: 11 }}>
                <input type="checkbox" checked={config.invertColorScale} onChange={(e) => update({ invertColorScale: e.target.checked })} />
                Invert scale
              </label>
            </>
          )}
        </Section>

        {/* Markers */}
        <Section title="Markers" theme={theme} defaultOpen={false}>
          <div style={row}>
            <span style={{ fontSize: 10, opacity: 0.6, width: 40 }}>Shape</span>
            <select value={config.markerShape} onChange={(e) => update({ markerShape: e.target.value as MarkerShape })} style={{ ...sel(theme), flex: 1 }}>
              {MARKER_SHAPE_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div style={row}>
            <span style={{ fontSize: 10, opacity: 0.6, width: 40 }}>Size</span>
            <input type="range" min={1} max={20} value={config.markerSize} onChange={(e) => update({ markerSize: Number(e.target.value) })} style={{ flex: 1 }} />
            <span style={{ fontSize: 10, width: 28, textAlign: 'right' }}>{config.markerSize}px</span>
          </div>
          <div style={row}>
            <span style={{ fontSize: 10, opacity: 0.6, width: 40 }}>Opacity</span>
            <input type="range" min={0.1} max={1} step={0.05} value={config.markerOpacity} onChange={(e) => update({ markerOpacity: Number(e.target.value) })} style={{ flex: 1 }} />
            <span style={{ fontSize: 10, width: 28, textAlign: 'right' }}>{Math.round(config.markerOpacity * 100)}%</span>
          </div>
          <label style={{ ...row, fontSize: 11 }}>
            <input type="checkbox" checked={config.showDataLabels} onChange={(e) => update({ showDataLabels: e.target.checked })} />
            Show data labels
          </label>
        </Section>

        {/* Lines */}
        {isLine && (
          <Section title="Line Style" theme={theme} defaultOpen={false}>
            <div style={row}>
              <span style={{ fontSize: 10, opacity: 0.6, width: 40 }}>Style</span>
              <select value={config.lineStyle} onChange={(e) => update({ lineStyle: e.target.value as LineStyle })} style={{ ...sel(theme), flex: 1 }}>
                {LINE_STYLE_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div style={row}>
              <span style={{ fontSize: 10, opacity: 0.6, width: 40 }}>Width</span>
              <input type="range" min={0.5} max={6} step={0.5} value={config.lineWidth} onChange={(e) => update({ lineWidth: Number(e.target.value) })} style={{ flex: 1 }} />
              <span style={{ fontSize: 10, width: 28, textAlign: 'right' }}>{config.lineWidth}px</span>
            </div>
          </Section>
        )}

        {/* Bar options */}
        {isBar && (
          <Section title="Bar Options" theme={theme} defaultOpen={false}>
            <div style={row}>
              <span style={{ fontSize: 10, opacity: 0.6, width: 50 }}>Mode</span>
              <select value={config.barMode} onChange={(e) => update({ barMode: e.target.value as BarMode })} style={{ ...sel(theme), flex: 1 }}>
                {BAR_MODE_OPTIONS.map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
            <div style={row}>
              <span style={{ fontSize: 10, opacity: 0.6, width: 50 }}>Opacity</span>
              <input type="range" min={0.2} max={1} step={0.05} value={config.barOpacity} onChange={(e) => update({ barOpacity: Number(e.target.value) })} style={{ flex: 1 }} />
              <span style={{ fontSize: 10, width: 28, textAlign: 'right' }}>{Math.round(config.barOpacity * 100)}%</span>
            </div>
            {config.chartType === 'histogram' && (
              <div style={row}>
                <span style={{ fontSize: 10, opacity: 0.6, width: 50 }}>Bins</span>
                <input type="range" min={5} max={100} value={config.histBins} onChange={(e) => update({ histBins: Number(e.target.value) })} style={{ flex: 1 }} />
                <span style={{ fontSize: 10, width: 28, textAlign: 'right' }}>{config.histBins}</span>
              </div>
            )}
          </Section>
        )}

        {/* Grid & Legend */}
        <Section title="Grid & Legend" theme={theme} defaultOpen={false}>
          <label style={{ ...row, fontSize: 11 }}>
            <input type="checkbox" checked={config.showGrid} onChange={(e) => update({ showGrid: e.target.checked })} /> Show grid
          </label>
          <label style={{ ...row, fontSize: 11 }}>
            <input type="checkbox" checked={config.showLegend} onChange={(e) => update({ showLegend: e.target.checked })} /> Show legend
          </label>
          {config.showLegend && (
            <select value={config.legendPosition} onChange={(e) => update({ legendPosition: e.target.value as LegendPosition })} style={sel(theme)}>
              {LEGEND_POSITION_OPTIONS.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          )}
        </Section>

        {/* Trend / Stats */}
        <Section title="Trend / Stats" theme={theme} defaultOpen={false}>
          <div style={row}>
            <span style={{ fontSize: 10, opacity: 0.6, width: 55 }}>Trend</span>
            <select value={config.trendLine} onChange={(e) => update({ trendLine: e.target.value as TrendLineType })} style={{ ...sel(theme), flex: 1 }}>
              {TREND_LINE_OPTIONS.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          {config.trendLine === 'polynomial' && (
            <div style={row}>
              <span style={{ fontSize: 10, opacity: 0.6, width: 55 }}>Order</span>
              <input type="number" min={2} max={10} value={config.trendLineOrder} onChange={(e) => update({ trendLineOrder: Number(e.target.value) })} style={numInp(theme)} />
            </div>
          )}
          <label style={{ ...row, fontSize: 11 }}>
            <input type="checkbox" checked={config.showMean} onChange={(e) => update({ showMean: e.target.checked })} /> Mean line (Y)
          </label>
          <label style={{ ...row, fontSize: 11 }}>
            <input type="checkbox" checked={config.showMedian} onChange={(e) => update({ showMedian: e.target.checked })} /> Median line (Y)
          </label>
          <label style={{ ...row, fontSize: 11 }}>
            <input type="checkbox" checked={config.showErrorBars} onChange={(e) => update({ showErrorBars: e.target.checked })} /> Error bars (±σ)
          </label>
          {(config.chartType === 'line' || config.chartType === 'scatter') && (
            <label style={{ ...row, fontSize: 11 }} title="Show slope of linear fit (e.g. lift curve slope for CL vs α)">
              <input type="checkbox" checked={config.showLiftCurveSlope} onChange={(e) => update({ showLiftCurveSlope: e.target.checked })} /> Lift curve slope (linear part)
            </label>
          )}
        </Section>

        {/* Contour options */}
        {config.chartType === 'contour' && (
          <Section title="Contour" theme={theme} defaultOpen={false}>
            <div style={row}>
              <span style={{ fontSize: 10, opacity: 0.6, width: 50 }}>Levels</span>
              <input type="range" min={3} max={30} value={config.contourLevels} onChange={(e) => update({ contourLevels: Number(e.target.value) })} style={{ flex: 1 }} />
              <span style={{ fontSize: 10, width: 20, textAlign: 'right' }}>{config.contourLevels}</span>
            </div>
            <label style={{ ...row, fontSize: 11 }}>
              <input type="checkbox" checked={config.contourSmooth} onChange={(e) => update({ contourSmooth: e.target.checked })} /> Smooth interpolation
            </label>
          </Section>
        )}

        {/* Layout / Export */}
        <Section title="Layout / Export" theme={theme} defaultOpen={false}>
          <div style={row}>
            <span style={{ fontSize: 10, opacity: 0.6, width: 55 }}>Aspect</span>
            <select value={config.aspectRatio} onChange={(e) => update({ aspectRatio: e.target.value as PlotBuilderConfig['aspectRatio'] })} style={{ ...sel(theme), flex: 1 }}>
              <option value="auto">Auto</option>
              <option value="16:9">16:9</option>
              <option value="4:3">4:3</option>
              <option value="1:1">1:1</option>
              <option value="3:4">3:4</option>
            </select>
          </div>
          <div style={row}>
            <span style={{ fontSize: 10, opacity: 0.6, width: 55 }}>Padding</span>
            <input type="range" min={10} max={80} value={config.plotPadding} onChange={(e) => update({ plotPadding: Number(e.target.value) })} style={{ flex: 1 }} />
            <span style={{ fontSize: 10, width: 28, textAlign: 'right' }}>{config.plotPadding}px</span>
          </div>
          <div style={row}>
            <span style={{ fontSize: 10, opacity: 0.6, width: 55 }}>DPI</span>
            <select value={config.dpi} onChange={(e) => update({ dpi: Number(e.target.value) })} style={{ ...sel(theme), flex: 1 }}>
              <option value={72}>72 (screen)</option>
              <option value={150}>150 (presentation)</option>
              <option value={300}>300 (print)</option>
            </select>
          </div>
          <div style={{ marginTop: 8, borderTop: `1px solid ${borderColor}`, paddingTop: 8 }}>
            <div style={lbl}>Provenance Tag</div>
            <select
              value={tagMode}
              onChange={(e) => setTagMode(e.target.value as TagMode)}
              style={sel(theme)}
            >
              <option value="qr">QR Code</option>
              <option value="aruco">ArUco Marker</option>
              <option value="both">QR + ArUco</option>
              <option value="none">None (metadata only)</option>
            </select>
            <button
              onClick={handleExport}
              disabled={exportState === 'exporting' || chartData.length === 0}
              style={{
                width: '100%', padding: '6px 12px', marginTop: 6,
                border: `1px solid ${borderColor}`, borderRadius: 5,
                background: exportState === 'done' ? '#1F7A4D'
                  : exportState === 'error' ? '#B5342A'
                  : theme === 'dark' ? '#1C211E' : '#FFFFFF',
                color: exportState === 'done' || exportState === 'error' ? '#FFFFFF'
                  : theme === 'dark' ? '#D1D9D3' : '#111813',
                fontWeight: 600, fontSize: 11,
                cursor: exportState === 'exporting' || chartData.length === 0 ? 'not-allowed' : 'pointer',
                opacity: exportState === 'exporting' || chartData.length === 0 ? 0.6 : 1,
                fontFamily: 'var(--fc-font-family)',
                transition: 'background 0.2s, opacity 0.2s',
              }}
            >
              {exportState === 'exporting' ? 'Exporting...'
                : exportState === 'done' ? 'Downloaded'
                : exportState === 'error' ? 'Export Failed'
                : 'Download PNG with Provenance'}
            </button>
          </div>
        </Section>

        </div>{/* end scrollable sections */}

        {/* Save — sticky footer, always reachable without scrolling */}
        <div style={{ padding: '12px', flexShrink: 0, borderTop: `1px solid ${borderColor}` }}>
          <div style={{ display: 'flex', gap: 4, marginBottom: 8 }}>
            {(["branch", "project"] as const).map((s) => (
              <button
                key={s}
                onClick={() => setScope(s)}
                style={{
                  flex: 1, padding: '5px 8px', fontSize: 10, fontWeight: 600,
                  border: `1px solid ${scope === s ? '#00643C' : borderColor}`,
                  borderRadius: 5,
                  background: scope === s
                    ? (theme === 'dark' ? '#0A3822' : '#E6F4ED')
                    : 'transparent',
                  color: theme === 'dark' ? '#D1D9D3' : '#111813',
                  cursor: 'pointer', fontFamily: 'var(--fc-font-family)',
                }}
              >
                {s === 'branch' ? 'This branch' : 'All branches'}
              </button>
            ))}
          </div>
          <button
            onClick={handleSave}
            disabled={saveState === 'saving' || !onSave}
            style={{
              width: '100%', padding: '8px 16px', border: 'none', borderRadius: 6,
              background: saveState === 'saved' ? '#1F7A4D'
                : saveState === 'error' ? '#B5342A'
                : '#00643C',
              color: '#FFFFFF', fontWeight: 600, fontSize: 12,
              cursor: saveState === 'saving' || !onSave ? 'not-allowed' : 'pointer',
              opacity: saveState === 'saving' || !onSave ? 0.7 : 1,
              fontFamily: 'var(--fc-font-family)',
              transition: 'background 0.2s, opacity 0.2s',
            }}
          >
            {saveState === 'saving' ? 'Saving…'
              : saveState === 'saved' ? '✓ Saved'
              : saveState === 'error' ? 'Save Failed'
              : 'Save Plot'}
          </button>
          <div style={{ fontSize: 10, opacity: 0.5, marginTop: 4, textAlign: 'center' }}>
            {saveState === 'error'
              ? 'Check console for details'
              : `Saves config + ${chartData.length} row snapshot`}
          </div>
        </div>
      </div>

      {/* PREVIEW */}
      <div ref={previewRef} style={{ flex: 1, minWidth: 0, minHeight: 0 }}>
        {chartData.length === 0 ? (
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%',
            color: theme === 'dark' ? '#666' : '#999', fontSize: 14,
          }}>
            No data available. Load a project to see the chart preview.
          </div>
        ) : (
          <Renderer data={chartData} config={config} theme={theme} onPointClick={onPointClick} selectedNodeId={selectedNodeId} />
        )}
      </div>
    </div>
  );
}

